import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronUp, Loader2, QrCode, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { PendingPatient, RejectionReason } from '../types/pendingPatient';
import { DuplicateWarning } from '../types/patient';
import { QRCodeModal } from '../components/QRCodeModal';

const REJECTION_REASONS: { value: RejectionReason; label: string }[] = [
  { value: 'duplicate', label: 'Duplicate patient' },
  { value: 'spam', label: 'Spam' },
  { value: 'incomplete', label: 'Incomplete information' },
  { value: 'other', label: 'Other' },
];

export function PendingRegistrationsPage() {
  const { get, post } = useApi();
  const [pending, setPending] = useState<PendingPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Expanded row for review
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateWarning[]>([]);
  const [dupLoading, setDupLoading] = useState(false);

  // Reject modal
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<RejectionReason>('duplicate');
  const [rejectNote, setRejectNote] = useState('');

  // Processing state
  const [processing, setProcessing] = useState<string | null>(null);

  // QR code modal
  const [showQR, setShowQR] = useState(false);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await get<PendingPatient[]>('/pending-patients?status=pending_review');
      if (result.success && result.data) {
        setPending(result.data);
      } else {
        setError(result.error || 'Failed to load');
      }
    } catch {
      setError('Failed to load pending registrations');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleExpand = useCallback(async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDuplicates([]);
      return;
    }
    setExpandedId(id);
    setDuplicates([]);
    setDupLoading(true);
    try {
      const result = await get<{ duplicates: DuplicateWarning[] }>(`/pending-patients/${id}/check-duplicates`);
      if (result.success && result.data) {
        setDuplicates(result.data.duplicates);
      }
    } catch {
      // non-critical
    } finally {
      setDupLoading(false);
    }
  }, [expandedId, get]);

  const handleApprove = useCallback(async (id: string) => {
    setProcessing(id);
    try {
      const result = await post<{ patientId: string }>(`/pending-patients/${id}/approve`);
      if (result.success) {
        setPending(prev => prev.filter(p => p.id !== id));
        setExpandedId(null);
        window.dispatchEvent(new Event('pending-count-changed'));
      } else {
        setError(result.error || 'Failed to approve');
      }
    } catch {
      setError('Failed to approve registration');
    } finally {
      setProcessing(null);
    }
  }, [post]);

  const handleReject = useCallback(async () => {
    if (!rejectId) return;
    setProcessing(rejectId);
    try {
      const result = await post(`/pending-patients/${rejectId}/reject`, {
        reason: rejectReason,
        note: rejectNote,
      });
      if (result.success) {
        setPending(prev => prev.filter(p => p.id !== rejectId));
        setRejectId(null);
        setRejectNote('');
        setExpandedId(null);
        window.dispatchEvent(new Event('pending-count-changed'));
      } else {
        setError(result.error || 'Failed to reject');
      }
    } catch {
      setError('Failed to reject registration');
    } finally {
      setProcessing(null);
    }
  }, [rejectId, rejectReason, rejectNote, post]);

  return (
    <main className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">Pending Registrations</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{pending.length} pending</span>
          <button
            onClick={() => setShowQR(true)}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
          >
            <QrCode className="h-4 w-4" /> QR Code
          </button>
        </div>
      </div>

      {showQR && (
        <QRCodeModal
          url={`${window.location.origin}/register`}
          onClose={() => setShowQR(false)}
        />
      )}

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      ) : pending.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No pending registrations.</div>
      ) : (
        <div className="space-y-3">
          {pending.map(p => (
            <div key={p.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Summary row */}
              <button
                onClick={() => handleExpand(p.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">
                      {p.firstName} {p.lastName}
                    </span>
                    <span className="text-xs text-gray-500">
                      {p.submittedAt ? new Date(p.submittedAt).toLocaleDateString('en-ZA') : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate mt-0.5">{p.reasonForVisit}</p>
                </div>
                {expandedId === p.id ? (
                  <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                )}
              </button>

              {/* Expanded detail */}
              {expandedId === p.id && (
                <div className="border-t border-gray-200 px-6 py-4">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <div>
                      <dt className="text-gray-500">ID Number</dt>
                      <dd className="font-medium text-gray-900">{p.idNumber || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Date of Birth</dt>
                      <dd className="font-medium text-gray-900">{p.dateOfBirth || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Phone</dt>
                      <dd className="font-medium text-gray-900">{p.phone || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Email</dt>
                      <dd className="font-medium text-gray-900">{p.email || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Medical Aid</dt>
                      <dd className="font-medium text-gray-900">
                        {p.medicalAidName
                          ? `${p.medicalAidName} (${p.medicalAidMemberNumber})`
                          : 'None / Private'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Reason for Visit</dt>
                      <dd className="font-medium text-gray-900">{p.reasonForVisit || '—'}</dd>
                    </div>
                  </dl>

                  {/* Duplicate warnings */}
                  {dupLoading ? (
                    <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" /> Checking for duplicates...
                    </div>
                  ) : duplicates.length > 0 ? (
                    <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-2">
                        <AlertTriangle className="h-4 w-4" /> Potential duplicates found
                      </div>
                      <ul className="space-y-1">
                        {duplicates.map((d, i) => (
                          <li key={i} className="text-sm text-amber-700">
                            {d.existingPatientName} — matched by {d.matchType === 'id_number' ? 'ID number' : 'name + DOB'}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="mt-4 text-sm text-green-700">No duplicates found.</div>
                  )}

                  {/* Action buttons */}
                  <div className="mt-5 flex items-center gap-3">
                    <button
                      onClick={() => handleApprove(p.id)}
                      disabled={processing === p.id}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {processing === p.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      Approve & Create Patient
                    </button>
                    <button
                      onClick={() => { setRejectId(p.id); setRejectReason('duplicate'); setRejectNote(''); }}
                      disabled={processing === p.id}
                      className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 flex items-center gap-2"
                    >
                      <X className="h-4 w-4" /> Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reject modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Reject Registration</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <select
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value as RejectionReason)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {REJECTION_REASONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                <textarea
                  rows={2}
                  value={rejectNote}
                  onChange={e => setRejectNote(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setRejectId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!!processing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {processing ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
